import { Slider } from "@mui/material";

/**
 * Wraps the MUI Slider component and customizes its appearance so it can be used as a progress bar for surveys.
 *
 * Refer to MUI Slider docs for more info on terms like track, rail, and thumb: https://mui.com/material-ui/react-slider/
 *
 * @param {object} props
 * @param {"normal" | "inverted" | false} props.track Determines how the track should be rendered. Pass in `false` to disable the track. Default is "normal". (See MUI Slider `track` prop).
 * @param {"horizontal" | "vertical"} props.orientation The orientation of the slider.
 * @param {number} props.value The current value of the slider.
 * @param {string} props.trackBackgroundColor Background color of the slider track.
 * @param {string} props.railBackgroundColor Background color of the slider rail.
 * @param {string} props.thumbBorderColor Border color of the slider thumb.
 * @param {number} [props.minValue=0] The minimum allowed value of the slider. Default is 0.
 * @param {number} props.maxValue The maximum allowed value of the slider.
 * @param {SliderProps["sx"]} [props.style] Custom styles to apply to the Slider component.
 */
function SurveyProgressBar({
    track,
    orientation,
    value,
    trackBackgroundColor,
    railBackgroundColor,
    thumbBorderColor,
    minValue = 0,
    maxValue,
    style,
}) {
    return (
        <Slider
            aria-label="survey-progress"
            aria-readonly={true}
            aria-hidden={true}
            track={track}
            orientation={orientation}
            sx={{
                // To disable the animation when the value changes, add transition: "none" to "& .MuiSlider-track", "& .MuiSlider-rail", and "& .MuiSlider-thumb"
                "& .MuiSlider-track": {
                    border: "none",
                    backgroundColor: trackBackgroundColor,
                    boxShadow: "0 8px 6px -6px black",
                },
                "& .MuiSlider-rail": {
                    border: "none",
                    backgroundColor: railBackgroundColor,
                },
                "& .MuiSlider-thumb": {
                    height: "24px",
                    width: "24px",
                    backgroundColor: "#fff",
                    border: `3px solid ${thumbBorderColor}`,
                    "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible": {
                        boxShadow: "inherit",
                    },
                },
                ...style,
            }}
            value={value}
            min={minValue}
            max={maxValue}
            disabled
        />
    );
}

export default SurveyProgressBar;
